"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Building2, Factory, HardHat, Waves, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
const sectors = [{
  id: 1,
  title: "Oil & Gas",
  description: "Mecanizado de precisión y soporte técnico crítico para refinerías, plataformas y transporte de crudo.",
  icon: Building2,
  titleClassName: "@container/card-header @container/card-header @container/card-header @container/card-header @container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 !bg-[url(https://mivgrupo.com/wp-content/uploads/2026/04/Screenshot_20260417_101027_WhatsApp.jpg)] relative z-10 pb-4 pt-8 !bg-[url(https://mivgrupo.com/wp-content/uploads/2026/04/Hero-Tanquero-1400.jpg)]"
}, {
  id: 2,
  title: "Industria Cementera",
  description: "Fabricación y reparación de componentes críticos para maquinaria pesada en plantas de producción continua.",
  icon: Factory,
  titleClassName: "@container/card-header @container/card-header @container/card-header @container/card-header @container/card-header @container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 !bg-[url(https://mivgrupo.com/wp-content/uploads/2026/04/industrial-cojinetes.png)] relative z-10 pb-4 pt-8 !bg-[url(https://mivgrupo.com/wp-content/uploads/2026/04/asa_monogram_watermark.webp)]"
}, {
  id: 3,
  title: "Petroquímica",
  description: "Mantenimiento de intercambiadores y fabricación de componentes bajo estrictas normas internacionales.",
  icon: HardHat,
  titleClassName: "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 relative z-10 pb-4 pt-8 !bg-[url(https://mivgrupo.com/wp-content/uploads/2026/04/Screenshot_20260419_104435_Gallery.jpg)]"
}, {
  id: 4,
  title: "Metalmecánica Pesada",
  description: "Mecanizado en sitio y producción de engranajes de gran escala para procesos de transformación industrial.",
  icon: Waves,
  titleClassName: "@container/card-header @container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 !bg-[url(https://subtle-ibex.10web.cloud/wp-content/uploads/2026/04/post_understanding-gear-reducers-and-their-role-in-heavy-industry.webp)] relative z-10 pb-4 pt-8 !bg-[url(https://mivgrupo.com/wp-content/uploads/2026/04/Vnexus-Machining.jpg)]"
}];
export default function SpecialtySectors() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="170453" id="sectores-de-especialidad" className="scroll-mt-10 md:scroll-mt-15 lg:scroll-mt-19 dark bg-background py-12 md:py-16 lg:py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-[0.05] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={cn("max-w-3xl mb-8 md:mb-12 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="h-px w-8 bg-primary block"></span>
            <span className="text-sm font-mono text-primary uppercase tracking-widest">
              Sectores de Operación Crítica
            </span>
          </div>
          <h2 className="font-serif font-bold tracking-tighter text-foreground mb-6 !text-4xl">Mecanizado y Fabricación: Impacto Industrial</h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-sans">
            En MIV, entregamos soluciones de mecanizado y fabricación de alta precisión para los sectores de Oil & Gas, Petroquímica e industria pesada, garantizando confiabilidad total y continuidad operativa.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sectors.map((sector, i) => <div key={sector.id} data-index={i} className={cn("transition-all duration-700 ease-out h-full", isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", i % 2 !== 0 ? "lg:mt-12" : "", i === 0 && "delay-[0ms]", i === 1 && "delay-[100ms]", i === 2 && "delay-[200ms]", i === 3 && "delay-[300ms]")}>
              <div className="block group h-full cursor-default outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg">
                <Card className="relative h-full overflow-hidden border-border/40 bg-card/40 backdrop-blur-md hover:border-primary/50 hover:bg-card/60 hover:shadow-2xl hover:shadow-primary/5 transition-all duration-500 rounded-lg py-0 flex flex-col">
                  
                  <sector.icon className="absolute -bottom-8 -right-8 size-48 text-muted-foreground/10 transition-colors duration-500 group-hover:text-primary/20 pointer-events-none" strokeWidth={1} aria-hidden="true" />

                  <CardHeader className={`${sector.titleClassName || ""} relative z-10 pb-4 pt-8`}>
                    <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                      <sector.icon className="size-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-serif tracking-tight group-hover:text-primary transition-colors duration-300 !text-[hsl(32,95%,44%)] !font-extrabold">
                      {sector.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="relative z-10 flex-grow">
                    <p className="text-muted-foreground leading-relaxed line-clamp-3 font-sans !text-base">
                      {sector.description}
                    </p>
                  </CardContent>
                  
                  <CardFooter className="relative z-10 pt-4 pb-8">
                    <span className="inline-flex items-center text-sm font-mono font-medium text-primary opacity-0 -translate-x-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0">
                      Ver Capacidades <ArrowRight className="ml-2 size-4" />
                    </span>
                  </CardFooter>
                </Card>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}